'use client';

import { WagmiConfig, createConfig } from 'wagmi';
import { bsc, bscTestnet } from 'wagmi/chains';
import { http, type Chain } from 'viem';
import { injected, walletConnect } from '@wagmi/connectors';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Get network ID from env
const networkId = Number(process.env.NEXT_PUBLIC_NETWORK_ID || 97);

// Select chain based on network ID
const selectedChain = networkId === 56 ? bsc : bscTestnet;

// RPC endpoints for the selected chain
const rpcUrls = networkId === 56 
  ? [
    'https://bsc-dataseed.binance.org/',
    'https://bsc-dataseed1.defibit.io/',
    'https://bsc-dataseed1.ninicoin.io/',
  ]
  : [
    'https://bsc-testnet.public.blastapi.io',
    // 'https://data-seed-prebsc-1-s1.binance.org:8545/',
    'https://data-seed-prebsc-2-s1.binance.org:8545/',
    'https://data-seed-prebsc-1-s2.binance.org:8545/',
  ];

// Configure the selected chain with RPC URLs
const configuredChain: Chain = {
  ...selectedChain,
  rpcUrls: {
    ...selectedChain.rpcUrls,
    default: { http: rpcUrls },
    public: { http: rpcUrls },
  }
};

if (!process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID) {
  throw new Error('Missing NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID');
}

const config = createConfig({
  chains: [configuredChain],
  transports: {
    [configuredChain.id]: http(rpcUrls[0]),
  },
  connectors: [
    injected(),
    ...(typeof window !== 'undefined' ? [
      walletConnect({
        projectId: process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID,
        showQrModal: true,
        metadata: {
          name: 'XoSo DApp',
          description: 'Decentralized Lottery Application',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://xoso.example.com',
          icons: ['https://xoso.example.com/favicon.ico']
        }
      })
    ] : []),
  ],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

export function Web3Provider({ children }: { children: React.ReactNode }) {
  return (
    <WagmiConfig config={config}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </WagmiConfig>
  );
}